import React, { useCallback, useEffect, useState } from "react";
import { MapPin, PencilLine, ShieldAlert } from "lucide-react";
import { PERMISSIONS } from "../../Permissions";
import fuelService from "../../services/fuel.service";
import { formatOutOfBoundRadius } from "../../utils/travelFormat";
import { t } from "../../locales/translate";
import EditOutOfBoundRadiusModal from "./EditOutOfBoundRadiusModal";
import "./TravelSummary.css";
import "./OutOfBoundRadiusCard.css";

const OutOfBoundRadiusCard = ({ refreshKey }) => {
  const permissions = JSON.parse(localStorage.getItem("permissionsList")) || [];

  const [outOfBoundRadius, setOutOfBoundRadius] = useState(null);
  const [showRadiusModal, setShowRadiusModal] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const canView = permissions.includes(PERMISSIONS.CAN_VIEW_OUT_OF_BOUND_RADIUS);
  const canUpdate = permissions.includes(PERMISSIONS.CAN_UPDATE_OUT_OF_BOUND_RADIUS);

  const fetchData = useCallback(async () => {
    if (!canView) return;
    setIsLoading(true);
    try {
      const res = await fuelService.getLatestOutOfBoundRadius();
      if (res.data?.status && res.data?.hasResult) {
        setOutOfBoundRadius(res.data.result[0]);
      }
    } catch {
      setOutOfBoundRadius(null);
    } finally {
      setIsLoading(false);
    }
  }, [canView]);

  useEffect(() => {
    if (canView) {
      fetchData();
    }
  }, [fetchData, canView, refreshKey]);

  if (!canView) {
    return null;
  }

  return (
    <>
      <div className="out-of-bound-card-container">
        <div className="out-of-bound-card-panel">
          <div className="travel-summary-header">
            <div className="stats-card-title">
              <ShieldAlert color="#1e293b" size={20} />
              <span>{t("outOfBoundPermission")}</span>
            </div>
          </div>
          <div className="out-of-bound-card-body">
          <div className="travel-summary-stats">
            {isLoading ? (
              <div className="travel-stat-card skeleton-pulse">
                <div className="travel-stat-card-header">
                  <span style={{ width: 18, height: 18, borderRadius: '50%', background: '#e2e8f0', display: 'inline-block' }} />
                  <span style={{ width: '60%', height: 14, borderRadius: 4, background: '#e2e8f0', display: 'inline-block', marginLeft: 8 }} />
                </div>
                <div className="travel-stat-value-row" style={{ marginTop: 12 }}>
                  <span style={{ width: '45%', height: 22, borderRadius: 4, background: '#e2e8f0', display: 'inline-block' }} />
                </div>
              </div>
            ) : (
              <div className="travel-stat-card">
                <div className="travel-stat-card-header">
                  <span className="travel-stat-icon">
                    <MapPin color="#EF4444" size={18} />
                  </span>
                  <span className="travel-stat-label">{t("outOfBoundRadius")}</span>
                </div>
                <div className="travel-stat-value-row">
                  <span className="travel-stat-value">
                    {formatOutOfBoundRadius(outOfBoundRadius?.radius)}
                  </span>
                  {canUpdate && (
                    <button
                      type="button"
                      className="travel-stat-edit"
                      onClick={() => setShowRadiusModal(true)}
                    >
                      <PencilLine size={14} />
                      {t("edit")}
                    </button>
                  )}
                </div>
              </div>
            )}
          </div>
          </div>
        </div>
      </div>

      {showRadiusModal && (
        <EditOutOfBoundRadiusModal
          editData={outOfBoundRadius}
          onClose={() => setShowRadiusModal(false)}
          onSave={(data) => {
            setOutOfBoundRadius(data);
            setShowRadiusModal(false);
          }}
        />
      )}
    </>
  );
};

export default OutOfBoundRadiusCard;
